import React, { useState, useContext, useEffect, useCallback } from 'react'
import Explorer from './Explorer'
import Notepad from './Notepad';
import DataContext from '../contexts/dataContext'
import Shortcuts from './Shortcuts';
import Player from './Player';
import Paint from './Paint';
import Internet from './Internet';
import HamsterDance from './HamsterDance';
import Ambience from './Ambience';
import Doom from './Doom';
import ChexQuest from './ChexQuest';
import Taskbar from './Taskbar';
import CreditsPanel from './CreditsPanel';
import { Alert, TitleBar } from '@react95/core';
import { Wangimg130, MediaCd } from '@react95/icons';
import { useClippy } from '@react95/clippy';
import Defrag from './Defrag';
import { useWindowManager } from '../contexts/windowContext';
import Win95Window from './Win95Window';

const funnyLines = [
    "everything's coming up clippy!",
    "this website is perfectly cromulent"
];

const PHOTO_ICON = <Wangimg130 variant="16x16_4" />;
const AMBIENCE_ICON = <MediaCd variant="16x16_4" />;

function Desktop({ onShutdown }) {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 850);
    useEffect(() => {
        const resize = () => setIsMobile(window.innerWidth < 850);
        window.addEventListener('resize', resize);
        return () => window.removeEventListener('resize', resize);
    }, []);

    const data = useContext(DataContext);
    const { clippy } = useClippy();
    const { focusWindow } = useWindowManager();

    const [explorerOpened, toggleExplorer] = useState(false);
    // Each file owns a stable window; reopening it focuses that instance rather
    // than replacing another document or resetting its scroll/drag position.
    const [openDocuments, setOpenDocuments] = useState([]);
    const [paintOpened, togglePaint] = useState(false);
    const [internetOpened, toggleInternet] = useState(false);
    const [doomOpened, toggleDoom] = useState(false);
    const [chexQuestOpened, toggleChexQuest] = useState(false);
    const [hamsterDanceOpened, toggleHamsterDance] = useState(false);
    const [recycleBinOpened, toggleRecycleBin] = useState(false);
    const [moreStuffOpened, toggleMoreStuff] = useState(false);
    const [secretStuffOpened, toggleSecretStuff] = useState(false);
    const [activeSelection, setActiveSelection] = useState(null);
    const [items, setItems] = useState([]);
    const [trashItems, setTrashItems] = useState([]);
    const [moreStuffItems, setMoreStuffItems] = useState([]);
    const [secretStuffItems, setSecretStuffItems] = useState([]);
    const [clockAlertOpened, toggleClockAlert] = useState(false);
    const [photoOpened, togglePhoto] = useState(false);
    const [defragOpened, toggleDefrag] = useState(false);
    const [ambienceOpened, toggleAmbience] = useState(false);
    const [creditsOpened, toggleCredits] = useState(false);

    useEffect(() => {
        if (clippy && !isMobile) {
            clippy.animate();
            clippy.delay(3000);

            
            clippy.speak(funnyLines[0]);
            clippy.delay(3000);

            const leaveAnimation = clippy.hasAnimation('GoodBye')
                ? 'GoodBye'
                : clippy.hasAnimation('Goodbye')
                    ? 'Goodbye'
                    : 'Hide';

            clippy.play(leaveAnimation, 5000, () => {
                clippy.hide(true, () => {});
            });

            return () => {
                clippy.stop();
            };
        }
    }, [clippy, isMobile]);

    useEffect(
        () => {
            const files = data.getFolderItems(null);
            const trash = data.getTrashItems();
            const moreStuff = data.getFolderItems('more-stuff');
            const secretStuff = data.getFolderItems('secret-stuff');

            setItems(files);
            setTrashItems(trash);
            setMoreStuffItems(moreStuff);
            setSecretStuffItems(secretStuff);

            setOpenDocuments([data.getItem('projects'), data.getItem('about')]);
        }, [data]);


    const closeExplorer = useCallback(() => {
        toggleExplorer(false);
    }, []);

    const openExplorer = useCallback(() => {
        toggleExplorer(true);
        focusWindow('explorer');
    }, [focusWindow]);

    const closeMoreStuff = useCallback(() => toggleMoreStuff(false), []);
    const closeSecretStuff = useCallback(() => toggleSecretStuff(false), []);
    const openDefrag = useCallback(() => {
        toggleDefrag(true);
        focusWindow('defrag');
    }, [focusWindow]);
    const closeDefrag = useCallback(() => toggleDefrag(false), []);

    const openFolder = (item) => {
        if (item.id === 'more-stuff') {
            toggleMoreStuff(true);
            focusWindow('more-stuff');
        } else if (item.id === 'secret-stuff') {
            toggleSecretStuff(true);
            focusWindow('secret-stuff');
        }
    }

    const closeNotepad = useCallback((id) => {
        setOpenDocuments(documents => documents.filter(document => document.id !== id));
    }, []);

    const openNotepad = useCallback((item) => {
        setOpenDocuments(documents => documents.some(document => document.id === item.id)
            ? documents : [...documents, item]);
        focusWindow(`notepad:${item.id}`);
    }, [focusWindow]);

    const openPaint = useCallback(() => {
        togglePaint(true);
        focusWindow('paint');
    }, [focusWindow]);

    const closePaint = useCallback(() => {
        togglePaint(false);
    }, []);

    const openRecycleBin = useCallback(() => {
        toggleRecycleBin(true);
        focusWindow('recycle-bin');
    }, [focusWindow]);

    const closeRecycleBin = useCallback(() => {
        toggleRecycleBin(false);
    }, []);

    const openInternet = useCallback(() => {
        toggleInternet(true);
        focusWindow('internet');
    }, [focusWindow]);

    const closeInternet = useCallback(() => {
        toggleInternet(false);
    }, []);

    const openDoom = useCallback(() => {
        toggleDoom(true);
        focusWindow('doom');
    }, [focusWindow]);

    const closeDoom = useCallback(() => {
        toggleDoom(false);
    }, []);

    const openChexQuest = useCallback(() => {
        toggleChexQuest(true);
        focusWindow('chex-quest');
    }, [focusWindow]);

    const closeChexQuest = useCallback(() => {
        toggleChexQuest(false);
    }, []);

    const openHamsterDance = useCallback(() => {
        toggleHamsterDance(true);
        focusWindow('hamster');
    }, [focusWindow]);

    const closeHamsterDance = useCallback(() => {
        toggleHamsterDance(false);
    }, []);

    const openAmbience = useCallback(() => {
        toggleAmbience(true);
        focusWindow('ambience');
    }, [focusWindow]);

    const closeAmbience = useCallback(() => {
        toggleAmbience(false);
    }, []);

    const openClockAlert = useCallback(() => toggleClockAlert(true), []);
    const closeClockAlert = useCallback(() => toggleClockAlert(false), []);
    const openCredits = useCallback(() => {
        toggleCredits(true);
        focusWindow('credits-help');
    }, [focusWindow]);
    const closeCredits = useCallback(() => toggleCredits(false), []);

    const openPhoto = useCallback(() => {
        togglePhoto(true);
        focusWindow('photo');
    }, [focusWindow]);
    const closePhoto = useCallback(() => togglePhoto(false), []);

    const handleBackgroundClick = useCallback((e) => {
        if (e.target === e.currentTarget) {
            setActiveSelection(null);
        }
    }, []);

    return (
        <div
            onClick={handleBackgroundClick}
            style={{
                height: '100vh',
                width: '100vw',
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: 0
            }}
        >
            <Shortcuts
                openExplorer={openExplorer}
                openPaint={openPaint}
                openNotepad={openNotepad}
                openRecycleBin={openRecycleBin}
                openInternet={openInternet}
                openDoom={openDoom}
                openChexQuest={openChexQuest}
                openHamsterDance={openHamsterDance}
                activeSelection={activeSelection}
                setActiveSelection={setActiveSelection}
                openPhoto={openPhoto}
                openDefrag={openDefrag}
                openAmbience={openAmbience}
            />
            {
                explorerOpened && (
                    <Explorer
                        items={items}
                        closeExplorer={closeExplorer}
                        openNotepad={openNotepad}
                        openFolder={openFolder}
                        isMobile={isMobile}
                        title="Stuff"
                        icon="Folder"
                        style={{
                            left: isMobile ? '5%' : '10%',
                            top: isMobile ? '10%' : '15%',
                        }}
                    />
                )
            }
            {
                moreStuffOpened && (
                    <Explorer
                        items={moreStuffItems}
                        closeExplorer={closeMoreStuff}
                        openNotepad={openNotepad}
                        openFolder={openFolder}
                        isMobile={isMobile}
                        title="More Stuff"
                        id="more-stuff"
                        icon="Folder"
                        style={{
                            left: isMobile ? '5%' : '20%',
                            top: '25%',
                        }}
                    />
                )
            }
            {
                secretStuffOpened && (
                    <Explorer
                        items={secretStuffItems}
                        closeExplorer={closeSecretStuff}
                        openNotepad={openNotepad}
                        openFolder={openFolder}
                        isMobile={isMobile}
                        title="Really Secret Stuff"
                        id="secret-stuff"
                        icon="Folder"
                        style={{
                            left: isMobile ? '5%' : '25%',
                            top: '30%',
                        }}
                    />
                )
            }
            {
                recycleBinOpened && (
                    <Explorer
                        items={trashItems}
                        closeExplorer={closeRecycleBin}
                        openNotepad={openNotepad}
                        openFolder={openFolder}
                        isMobile={isMobile}
                        title="Recycle Bin"
                        id="recycle-bin"
                        icon="RecycleFull"
                        style={{
                            left: isMobile ? '5%' : '15%',
                            top: isMobile ? '15%' : '20%',
                        }}
                    />
                )
            }
            {openDocuments.map((item, index) => (
                <Notepad
                    key={item.id}
                    id={`notepad:${item.id}`}
                    closeNotepad={() => closeNotepad(item.id)}
                    selectedItem={item}
                    style={item.id === 'projects' ? {
                        left: isMobile ? '100px' : 'calc(50% - 600px)',
                        top: isMobile ? '15%' : 'calc(10% + 200px)',
                    } : {
                        left: `calc(50% - 300px + ${Math.max(0, index - 1) * 24}px)`,
                        top: `calc(10% + ${Math.max(0, index - 1) * 24}px)`,
                    }}
                />
            ))}
            {
                paintOpened && (
                    <Paint closePaint={closePaint} />
                )
            }
            {
                internetOpened && (
                    <Internet closeInternet={closeInternet} />
                )
            }
            {
                doomOpened && (
                    <Doom closeDoom={closeDoom} isMobile={isMobile} />
                )
            }
            {
                chexQuestOpened && (
                    <ChexQuest closeChexQuest={closeChexQuest} isMobile={isMobile} />
                )
            }
            {
                hamsterDanceOpened && (
                    <HamsterDance closeHamsterDance={closeHamsterDance} />
                )
            }
            {
                ambienceOpened && (
                    <Win95Window
                        id="ambience"
                        icon={AMBIENCE_ICON}
                        title="Ambience"
                        onClose={closeAmbience}
                        draggable={false}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: '28px',
                            width: '100vw',
                            height: 'calc(100vh - 28px)',
                            transform: 'none',
                            boxSizing: 'border-box',
                            userSelect: 'none'
                        }}
                    >
                        <Ambience />
                    </Win95Window>
                )
            }
            {
                clockAlertOpened && (
                    <Alert
                        title="System Message"
                        type="error"
                        message="don't click the clock!"
                        titleBarOptions={[
                            <TitleBar.Close key="close" onClick={closeClockAlert} />
                        ]}
                        buttons={[
                            {
                                value: "OK",
                                onClick: closeClockAlert,
                            },
                        ]}
                        style={{
                            position: 'absolute',
                            top: '10px',
                            left: '10px',
                            width: '300px',
                            zIndex: 1000000
                        }}
                    />
                )
            }
            {
                photoOpened && (
                    <Win95Window
                        id="photo"
                        icon={PHOTO_ICON}
                        title="hi-res-travel-photo.jpg"
                        onClose={closePhoto}
                        style={{
                            width: 'auto',
                            maxWidth: '90vw',
                            height: 'auto',
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            userSelect: 'none'
                        }}
                    >
                        <div style={{ padding: '10px', display: 'flex', justifyContent: 'center', backgroundColor: '#c0c0c0', userSelect: 'none' }}>
                            {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                            <img
                                src={`${process.env.PUBLIC_URL}/images/hi-res-travel-photo.jpg`}
                                alt="High Resolution Travel Photo"
                                style={{ maxWidth: '100%', height: 'auto', imageRendering: 'pixelated' }}
                            />
                        </div>
                    </Win95Window>
                )
            }
            {
                defragOpened && (
                    <Defrag closeDefrag={closeDefrag} />
                )
            }
            {
                creditsOpened && (
                    <CreditsPanel onClose={closeCredits} isMobile={isMobile} />
                )
            }
            <Player />
            <Taskbar
                onClockClick={openClockAlert}
                onShutdown={onShutdown}
                openCredits={openCredits}
                openPaint={openPaint}
                openNotepad={openNotepad}
                openDoom={openDoom}
                openChexQuest={openChexQuest}
                openPhoto={openPhoto}
                openAmbience={openAmbience}
            />
        </div>
    )
}

export default Desktop
